import { useCallback, useEffect, useRef, useState } from "react";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { AlertTriangle, Info } from "lucide-react";
import { trackToolCompleted } from "@/lib/analytics";
import { formatFileSize } from "@/lib/formatFileSize";
import { plural } from "@/lib/plural";
import { useRunToken } from "@/hooks/useRunToken";
import { loadPdfDocument } from "./utils";
import { getPdfPageCount, parseMultiRange } from "./operations";
import { Input } from "@/components/ui/input";
import { stemOfPdfName } from "./pdfMetadata";
import { ConversionCancelled, convertPdfToWord, pdfSubset, sortedPageNumbers, type ConvertSummary, type TesseractWorker } from "./pdfToWord";
import { ConvertedPreview, type PreviewPage } from "./ConvertedPreview";
import { OCR_LANGUAGES } from "./ocrText";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Converts a PDF into an editable Microsoft Word (.docx) document. Paragraphs stay paragraphs, with the real font size, bold, italic and colour; bullet lists become Word lists; text in two or three columns is read column by column; tables are rebuilt as real Word tables (numbers stay in their own columns); pictures are placed where they were; web links stay clickable; filled-in form fields (names, ticked boxes) are written out; running headers and page numbers go into the Word header and footer; and each page keeps its own size, so Letter, A4 and landscape pages come out the right shape. Pages that are scans or photos with no selectable text are read with OCR page by page, so a scanned page inside an otherwise normal PDF is not left blank.`,
  how: `Drop your PDF, choose the language of any scanned pages if it isn't English, optionally type the pages you want (like 1-3,6; empty means all), and click Convert to Word. When it finishes you get a short summary (pages, tables, pictures, anything that could not be included), a preview of the first pages, and the .docx to download. Everything runs in your browser; the file is not uploaded.`,
  tip: `The result is a normal Word document whose text reflows as you edit; it is not a pixel-exact copy of the PDF page, so small spacing and line-break differences are expected. Check anything important in the preview or in Word. Scanned pages are read with OCR, which is slower (several seconds a page, and the first scan downloads the language data) and can misread poor scans; pick the right language first, because the wrong language gives wrong characters. Numbered lists keep their numbers as typed text. Text that sits on top of a picture in the PDF stays part of the picture when the picture itself is rotated or unusual. Text in colour patterns or special colour spaces comes out in the default colour rather than a wrong one.`,
};

export default function PdfToWord() {
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ blob: Blob; summary: ConvertSummary; preview: PreviewPage[]; forFile: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [ocrLanguage, setOcrLanguage] = useState("eng");
  const [pageCount, setPageCount] = useState(0);
  const [rangeText, setRangeText] = useState("");
  const runs = useRunToken();
  const workerRef = useRef<TesseractWorker | null>(null);

  const killWorker = useCallback(() => {
    const w = workerRef.current;
    workerRef.current = null;
    if (w) void w.terminate().catch(() => undefined);
  }, []);

  // Leaving the page ends any run in progress and its OCR engine.
  useEffect(
    () => () => {
      runs.cancel();
      killWorker();
    },
    [runs, killWorker],
  );

  const handleFiles = useCallback(
    (files: File[]) => {
      runs.cancel();
      killWorker();
      setProcessing(false);
      setFile(files[0] || null);
      setResult(null);
      setErrorMessage("");
      setRangeText("");
      setPageCount(0);
      if (files[0]) getPdfPageCount(files[0]).then(setPageCount).catch(() => undefined); // a nicety: conversion reports its own problems
    },
    [runs, killWorker],
  );

  const reset = () => {
    runs.cancel();
    killWorker();
    setProcessing(false);
    setFile(null);
    setResult(null);
    setErrorMessage("");
    setRangeText("");
    setPageCount(0);
    setProgress(0);
    setStatusMessage("");
  };

  const handleStop = () => {
    runs.cancel();
    killWorker();
    setProcessing(false);
    setStatusMessage("");
    setProgress(0);
  };

  const convert = async () => {
    if (!file) return;
    let wanted: number[] | null = null;
    if (rangeText.trim()) {
      if (!pageCount) { setErrorMessage("Still reading the page count. Wait a moment and try again, or clear the page range."); return; }
      const r = parseMultiRange(rangeText, pageCount);
      if ("error" in r) { setErrorMessage(`Pages to convert: ${r.error}`); return; }
      wanted = sortedPageNumbers(r.pages);
    }
    const run = runs.start();
    setProcessing(true);
    setResult(null);
    setErrorMessage("");
    setProgress(0);
    setStatusMessage("Opening the PDF…");
    try {
      const pdf = await loadPdfDocument(file);
      if (!runs.isCurrent(run)) return;
      const outcome = await convertPdfToWord(
        wanted ? pdfSubset(pdf, wanted) : pdf,
        { ocrLanguage, fallbackTitle: stemOfPdfName(file.name) },
        {
          isCurrent: () => runs.isCurrent(run),
          status: (m) => runs.isCurrent(run) && setStatusMessage(m),
          progress: (f) => runs.isCurrent(run) && setProgress(f),
          registerWorker: (w) => {
            if (runs.isCurrent(run)) workerRef.current = w;
          },
        },
      );
      if (!runs.isCurrent(run)) return;
      if (!outcome) {
        setErrorMessage(
          "No readable text or pictures were found on any page, even with OCR. The PDF may be blank, too low in quality to read, or in a different language than the one selected.",
        );
        return;
      }
      setResult({ ...outcome, forFile: file.name });
      toast.success("PDF converted to Word");
      trackToolCompleted("pdf-to-word", "pdf-tools");
    } catch (error) {
      if (error instanceof ConversionCancelled || !runs.isCurrent(run)) return;
      console.error(error);
      const detail = error instanceof Error ? error.message : String(error);
      setErrorMessage(`Conversion failed: ${detail}`);
    } finally {
      if (runs.isCurrent(run)) {
        setProcessing(false);
        setStatusMessage("");
      }
    }
  };

  const s = result?.summary;
  const summaryParts = s
    ? [
        plural(s.pages, "page"),
        s.tables ? plural(s.tables, "table") : null,
        s.images ? plural(s.images, "picture") : null,
        s.links ? plural(s.links, "link") : null,
        s.formValues ? plural(s.formValues, "form field value") : null,
        s.ocrPages ? `${plural(s.ocrPages, "page")} read with OCR` : null,
      ].filter(Boolean)
    : [];

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload
          accept=".pdf"
          onFiles={handleFiles}
          label="Drop a PDF to convert to Word"
          description="Extracts real, editable text — processed entirely in your browser"
        />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card">
            <p className="text-sm font-medium break-all">{file.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{formatFileSize(file.size)}</p>
          </div>

          {processing ? (
            <div className="space-y-2">
              <ProcessingState message={statusMessage || "Working…"} onStop={handleStop} progress={progress > 0 ? progress * 100 : undefined} />
            </div>
          ) : result ? (
            <div className="space-y-4">
              <Card className="p-4 space-y-2" aria-live="polite">
                <p className="text-sm font-medium">Converted: {summaryParts.join(" · ")}</p>
                {s!.warnings.map((w) => (
                  <NoticeBox key={w} className="text-xs flex gap-1.5">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    {w}
                  </NoticeBox>
                ))}
              </Card>
              <div className="flex flex-wrap gap-3">
                <DownloadButton blob={result.blob} fileName={`${stemOfPdfName(result.forFile)}.docx`} label="Download Word File" />
                <Button variant="outline" onClick={reset}>
                  Convert another
                </Button>
              </div>
              {result.preview.length > 0 && (
                <details open className="rounded-lg border p-3">
                  <summary className="cursor-pointer text-sm font-medium">
                    Preview of the first {result.preview.length === 1 ? "page" : `${result.preview.length} pages`}
                  </summary>
                  <div className="mt-3 max-h-[28rem] overflow-y-auto">
                    <ConvertedPreview pages={result.preview} />
                    <p className="text-xs text-muted-foreground mt-2">A quick look at the text, styling and tables. Word lays the page out itself, so spacing will differ slightly.</p>
                  </div>
                </details>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <label htmlFor="ocr-language" className="text-muted-foreground">
                  Language of scanned pages (only used for pages with no selectable text):
                </label>
                <select
                  id="ocr-language"
                  value={ocrLanguage}
                  onChange={(e) => setOcrLanguage(e.target.value)}
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                >
                  {OCR_LANGUAGES.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <label htmlFor="word-pages" className="text-muted-foreground">Pages to convert{pageCount ? ` (the PDF has ${pageCount})` : ""}:</label>
                <Input id="word-pages" value={rangeText} onChange={(e) => { setRangeText(e.target.value); setErrorMessage(""); }} placeholder="All pages, or like 1-3,6" className="h-9 w-48" data-testid="word-pages" />
              </div>
              {errorMessage && (
                <Card className="p-3 border-red-300 bg-red-50 dark:bg-red-950/30" role="alert">
                  <div className="flex gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800 dark:text-red-300">{errorMessage}</p>
                  </div>
                </Card>
              )}
              <div className="flex gap-3">
                <Button onClick={convert}>Convert to Word</Button>
                <Button variant="outline" onClick={reset}>
                  Reset
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
